/* eslint-disable max-statements */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import type {
  AnimationTrigger,
  AvatarAnimation,
  SceneAnimation,
  SceneAnimationReference,
  AvatarTransitionCallback,
  IntroOutroAnimation
} from '@/customTypes'
import { usePreloadStore } from '@/stores/PreloadStore'

import Logger from '@/services/Logger.js'
const logger: any = new Logger('EffectsStore', true)

export const useEffectsStore = defineStore('animation', () => {
  // Note: State
  const preloadStore = usePreloadStore()

  const animationTriggers: Ref<AnimationTrigger[]> = ref([])
  const avatarAnimationQueue = ref<AvatarAnimation[]>([])
  const avatarAnimationState: Ref<AvatarAnimation> = ref('')
  const sceneAnimationPlaying: Ref<boolean> = ref(false)
  const sceneAnimationState: Ref<SceneAnimation> = ref(null)
  const loadedSceneAnimations: Ref<SceneAnimationReference[]> = ref([])
  const avatarTransitionCallback: Ref<AvatarTransitionCallback> = ref({ during: null, after: null })
  const rewardAnimationTrigger: Ref<boolean> = ref(false)
  const backgroundAnimationState: Ref<IntroOutroAnimation> = ref(null)
  const backgroundImage: Ref<{ current: string; next: string }> = ref({ current: '', next: '' })
  const backgroundBlur: Ref<boolean> = ref(false)
  let animationsTimingData: any = {}

  const introOutroAnimations: Ref<string[]> = ref([])
  const sceneAnimations: Ref<string[]> = ref([])
  const avatarAnimations: Ref<string[]> = ref([])
  const talkDisabledAnimations: Ref<string[]> = ref([])

  const avatarDefaultIdleAnimations: Ref<AvatarAnimation[]> = ref([])
  const avatarTransitionAnimations: Ref<AvatarAnimation[]> = ref([])
  const avatarPromptAnimation: Ref<AvatarAnimation | undefined> = ref(undefined)

  // Note: Actions
  function processNextAnimation() {
    if (avatarAnimationQueue.value?.length > 0) {
      const nextAnimation = avatarAnimationQueue.value.shift()
      if (nextAnimation) {
        setAvatarAnimationState(nextAnimation)
        return avatarAnimationState
      }
    } else return null
  }

  function getAnimation(animationName: string, animationType = 'avatar') {
    const animations = animationsTimingData[animationType]
    if (!animations || !animations[animationName]) return null

    const animation = animations[animationName]
    const startingFrame = animation.start
    return {
      start: startingFrame,
      end: animation.end,
      break: animation.break ? animation.break : null,
      animation: animationName,
      audio: animation.audio || null
    }
  }

  function isTalkDisabledAnimation(animationName: string) {
    return talkDisabledAnimations.value.includes(animationName)
  }

  function isInTransition(animation: AvatarAnimation): boolean {
    return animation.startsWith('IN')
  }

  function isOutTransition(animation: AvatarAnimation): boolean {
    return animation.startsWith('OUT')
  }

  function getRandomAnimation(animation: AvatarAnimation[]): AvatarAnimation {
    const randomIndex = Math.floor(Math.random() * animation.length)
    return animation[randomIndex]
  }

  function getDefaultIdleAnimation(): AvatarAnimation {
    return getRandomAnimation(avatarDefaultIdleAnimations.value)
  }

  function getTransitionAnimation(direction: 'IN' | 'OUT'): AvatarAnimation {
    const randomTransition = getRandomAnimation(avatarTransitionAnimations.value)
    return `${direction}${randomTransition}`
  }

  // Note: Mutations

  function setAnimationTimingData(data: any) {
    animationsTimingData = data

    if (animationsTimingData.introOutro) {
      introOutroAnimations.value = Object.keys(animationsTimingData.introOutro)
      introOutroAnimations.value.forEach((key) => {
        const animation = animationsTimingData.introOutro[key]
        const hasAudio = animation.audio

        if (hasAudio) {
          const animationAudio = `${key}.mp3`
          preloadStore.preloadAudioAsset(
            animationAudio,
            'tracks-and-sound-effects',
            key,
            hasAudio.volume
          )
        }
      })
    }

    if (animationsTimingData.scene) {
      sceneAnimations.value = Object.keys(animationsTimingData.scene)
      sceneAnimations.value.forEach((key) => {
        const animation = animationsTimingData.scene[key]
        const hasAudio = animation.audio

        if (hasAudio) {
          const animationAudio = `${key}.mp3`
          preloadStore.preloadAudioAsset(
            animationAudio,
            'tracks-and-sound-effects',
            key,
            hasAudio.volume
          )
        }
      })
    }
    if (animationsTimingData.avatar) {
      avatarAnimations.value = Object.keys(animationsTimingData.avatar)
      // Preload audio for animations
      avatarAnimations.value.forEach((key) => {
        const animation = animationsTimingData.avatar[key]
        const hasAudio = animation.audio

        if (hasAudio) {
          const animationAudio = `${key}.mp3`
          preloadStore.preloadAudioAsset(
            animationAudio,
            'tracks-and-sound-effects',
            key,
            hasAudio.volume
          )
        }
      })
    }
    if (animationsTimingData.talkDisabled) {
      talkDisabledAnimations.value = animationsTimingData.talkDisabled
    }
  }

  function setAnimationTriggers(triggers: AnimationTrigger[]) {
    logger.info('Setting animation triggers', triggers)
    animationTriggers.value = [...triggers]
  }

  function removeAnimationTrigger(index: number) {
    logger.info('Removing animation trigger', index)
    animationTriggers.value.splice(index, 1)
  }

  function setRewardAnimationTrigger(state: boolean) {
    logger.info('Triggering reward animation')
    rewardAnimationTrigger.value = state
  }

  //NOTE: Avatar Mutations

  function setDefaultIdleAnimations(animation: AvatarAnimation[]) {
    logger.info('Setting default idle animations', animation)
    const animationsArray = Array.isArray(animation) ? animation : [animation]
    avatarAnimationState.value = getRandomAnimation(animationsArray)
    avatarDefaultIdleAnimations.value = animationsArray
  }

  function setPromptAnimation(animation: AvatarAnimation | undefined) {
    logger.info('Setting prompt animation', animation)
    avatarPromptAnimation.value = animation
  }

  function setTransitionAnimation(animationSets: AvatarAnimation[]) {
    logger.info('Setting transition animation sets', animationSets)
    const animationsArray = Array.isArray(animationSets) ? animationSets : [animationSets]
    avatarTransitionAnimations.value = animationsArray
  }

  // Sets callback functions to happen during or after avatar transition / relocation
  function setAvatarTransitionCallback(
    during: (() => void) | null,
    after: (() => void) | null | undefined = undefined
  ) {
    avatarTransitionCallback.value.during = during
    if (after !== undefined) avatarTransitionCallback.value.after = after
  }

  function queueAvatarAnimation(state: AvatarAnimation | undefined, priority: boolean) {
    if (!state) return
    logger.info('queueAvatarAnimation', state, priority)
    if (priority) {
      if (avatarAnimationQueue.value.length === 1 && avatarAnimationQueue.value[0] === state) {
        avatarAnimationQueue.value = []
        queueAvatarAnimation(state, true)
      } else avatarAnimationQueue.value = [state]
    } else avatarAnimationQueue.value = [...avatarAnimationQueue.value, state]
  }

  function setAvatarAnimationState(state: AvatarAnimation) {
    avatarAnimationState.value = state
  }

  //NOTE: Scene Mutations
  function setSceneAnimationState(state: SceneAnimation) {
    logger.info('Setting scene animation state', state)
    sceneAnimationState.value = state
  }

  function setLoadedSceneAnimations(animations: SceneAnimationReference[]) {
    const uniqueAnimations: SceneAnimationReference[] = []

    for (const animation of animations) {
      const isAlreadyLoaded = loadedSceneAnimations.value.some(
        (anim) => anim.name === animation.name
      )
      if (!isAlreadyLoaded) uniqueAnimations.push(animation)
    }

    loadedSceneAnimations.value = [...loadedSceneAnimations.value, ...uniqueAnimations]
  }

  function setSceneAnimationPlaying(state: boolean) {
    sceneAnimationPlaying.value = state
  }

  function setBackgroundAnimationState(animation: IntroOutroAnimation) {
    logger.info('Setting background animation state', animation)
    backgroundAnimationState.value = animation
  }

  function setNextBackgroundImage(imageUrl: string) {
    if (backgroundImage.value.current === '') {
      backgroundImage.value.current = imageUrl
    }
    backgroundImage.value.next = imageUrl
  }

  function applyBackgroundImage(imageUrl: string | null = null) {
    if (
      (backgroundImage.value.next === '' &&
        imageUrl === null &&
        backgroundImage.value.current !== imageUrl) ||
      (backgroundImage.value.next !== '' &&
        backgroundImage.value.current !== '' &&
        backgroundImage.value.next === backgroundImage.value.current)
    ) {
      return
    }
    backgroundImage.value.current = !imageUrl ? backgroundImage.value.next : imageUrl
  }

  function setBackgroundBlur(state: boolean) {
    logger.info('Setting background blur state', state)
    backgroundBlur.value = state
  }

  function resetForScreenerIntro() {
    $reset()
    avatarAnimationQueue.value = []
    setAvatarAnimationState(getDefaultIdleAnimation())
    setSceneAnimationState(null)
    setBackgroundAnimationState(null)
    setNextBackgroundImage('')
    setBackgroundBlur(false)
  }

  function resetForScreenerOutro() {
    avatarAnimationQueue.value = []
    $reset()
    setAvatarAnimationState(getDefaultIdleAnimation())
    setSceneAnimationState(null)
    setBackgroundAnimationState(null)
    setNextBackgroundImage('')
    setBackgroundBlur(false)
  }

  function resetForSubtestReward() {
    // Clear existing animations and set up for subtest reward
    avatarAnimationQueue.value = []
    setAvatarAnimationState(getDefaultIdleAnimation())
    setSceneAnimationState(null)
    setBackgroundAnimationState(null)
    loadedSceneAnimations.value = []
    animationTriggers.value = []
    setBackgroundBlur(false)
    // Trigger reward animation
    setRewardAnimationTrigger(true)
  }

  function $reset() {
    // Avatar
    avatarAnimationState.value = getDefaultIdleAnimation()
    avatarAnimationQueue.value = []
    avatarTransitionCallback.value = { during: null, after: null }

    // Scene
    sceneAnimationState.value = null
    sceneAnimationPlaying.value = false
    sceneAnimationState.value = null
    rewardAnimationTrigger.value = false
    backgroundAnimationState.value = null
    backgroundImage.value = { current: '', next: '' }
    backgroundBlur.value = false
  }

  return {
    animationTriggers,
    rewardAnimationTrigger,
    introOutroAnimations,
    sceneAnimations,
    avatarAnimations,
    removeAnimationTrigger,
    setAnimationTriggers,
    setRewardAnimationTrigger,
    setAnimationTimingData,
    isTalkDisabledAnimation,
    getAnimation,
    // Avatar
    avatarAnimationState,
    avatarAnimationQueue,
    avatarTransitionCallback,
    avatarDefaultIdleAnimations,
    setDefaultIdleAnimations,
    setPromptAnimation,
    setTransitionAnimation,
    setAvatarAnimationState,
    setAvatarTransitionCallback,
    getDefaultIdleAnimation,
    getTransitionAnimation,
    isInTransition,
    isOutTransition,
    processNextAnimation,
    queueAvatarAnimation,
    getPromptAnimation: () => avatarPromptAnimation.value,
    // Scene
    sceneAnimationState,
    sceneAnimationPlaying,
    loadedSceneAnimations,
    backgroundAnimationState,
    backgroundImage,
    setNextBackgroundImage,
    applyBackgroundImage,
    setSceneAnimationState,
    setSceneAnimationPlaying,
    setLoadedSceneAnimations,
    setBackgroundAnimationState,
    backgroundBlur,
    setBackgroundBlur,
    // Resets
    $reset,
    resetForScreenerIntro,
    resetForScreenerOutro,
    resetForSubtestReward
  }
})
